import axios, { AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { store } from "../redux/store";
import { screens } from "../config/routes";
import environment from "../environment/environment";

/**
 * Axios instance with default configuration.
 * @type {import('axios').AxiosInstance}
 */
const axiosInstance: AxiosInstance = axios.create({
  baseURL: environment.baseUrl,
  headers: {
    "Cache-Control": "no-cache, no-store, must-revalidate",
    Pragma: "no-cache",
    Accept: "application/json",
    Expires: "0",
  },
});

/**
 * Axios interceptor for the request to add the authentication token.
 * @param {InternalAxiosRequestConfig<any>} config - The Axios request config.
 * @returns {Promise<InternalAxiosRequestConfig<any>>} The modified Axios request config with authentication token.
 */
axiosInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig<any>) => {
    const tempConfig = { ...config };
    tempConfig.timeout = 30000;
    tempConfig.timeoutErrorMessage = "timeout";

    // Add authorization token if available
    const state = store.getState();
    const token = state.auth.currentUser?.token;
    if (token) {
      tempConfig.headers.Authorization = `Bearer ${token}`;
    }

    return tempConfig;
  },
  (error) => {
    Promise.reject(error);
  },
);

/**
 * Axios interceptor for the response to handle 401 unauthorized responses.
 * @param {import('axios').AxiosResponse} response - The Axios response.
 * @returns {Promise<import('axios').AxiosResponse>} The original Axios response.
 */
axiosInstance.interceptors.response.use(
  async (response) => {
    return response;
  },
  async (error) => {
    if (error.response?.status === 401) {
      // Clear auth state
      store.dispatch({ type: "auth/setCurrentUser", payload: null });

      // Redirect to login
      window.location.href = screens.login;
    }
    return Promise.reject(error);
  },
);

export default axiosInstance;
